import React from 'react';
import { Link } from 'react-router-dom';
import {
  Monitor,
  Wifi,
  Coffee,
  BookOpen,
  Users,
  Shield,
  Clock,
  MapPin,
  Car,
  Bus,
} from 'lucide-react';
import { SEO } from '../../../shared/components';

// --- Data Structures ---
interface Facility {
    icon: React.ReactNode;
    title: string;
    description: string;
}

const facilities: Facility[] = [
    {
        icon: <Monitor className="w-6 h-6 text-accent" />,
        title: "Modern Classrooms in Al Wakra",
        description: "Spacious, well-lit classrooms equipped with smart boards and comfortable seating for CBSE, IGCSE, and IB tutoring in Qatar.",
    },
    {
        icon: <Wifi className="w-6 h-6 text-accent" />,
        title: "Technology Resources",
        description: "Computers, tablets, and specialized educational software for interactive K-12 learning and board exam preparation.",
    },
    {
        icon: <Coffee className="w-6 h-6 text-accent" />,
        title: "Student Lounge",
        description: "Comfortable space for relaxation between tutoring sessions with refreshments and casual peer interaction in Al Wakra.",
    },
    {
        icon: <BookOpen className="w-6 h-6 text-accent" />,
        title: "Resource Library",
        description: "Curated collection of CBSE, IGCSE, IB textbooks, reference materials, and digital resources for Qatar students.",
    },
    {
        icon: <Users className="w-6 h-6 text-accent" />,
        title: "Group Study Areas",
        description: "Dedicated collaborative learning spaces in Al Wakra for peer-to-peer teaching and group study sessions.",
    },
    {
        icon: <Shield className="w-6 h-6 text-accent" />,
        title: "Safety & Security",
        description: "24/7 surveillance, secure access control, and comprehensive emergency protocols for student safety in Qatar.",
    },
];

const operatingHours = [
    { day: "Sunday - Thursday", hours: "8:00 AM - 9:00 PM", highlight: false },
    { day: "Saturday", hours: "8:00 AM - 5:00 PM", highlight: false },
    { day: "Friday", hours: "Closed", highlight: true },
];

const FacilityCard: React.FC<{ facility: Facility, delay: number }> = ({ facility, delay }) => (
    <div className={`bg-white rounded-xl shadow-lg text-foreground p-6 animate-slide-up animation-delay-${delay}`}>
        <div className="w-14 h-14 rounded-full bg-cyan-100 flex items-center justify-center mb-4">
            {facility.icon}
        </div>
        <h3 className="text-xl font-display font-bold mb-3">{facility.title}</h3>
        <p className="text-muted-foreground">{facility.description}</p>
    </div>
);

const CampusPage: React.FC = () => {
    return (
        <div className="bg-accent text-white min-h-screen">
            <SEO
                title="Campus & Facilities - Modern Learning Center in Al Wakra | Tuition Point Qatar"
                description="Visit Tuition Point's modern campus in Al Wakra, Qatar. Smart classrooms, technology resources, library, and safe learning environment for CBSE, IGCSE, IB K-12 students."
                keywords="tuition center campus Qatar, Al Wakra education facilities, modern classrooms Qatar, tutoring center location, learning environment Al Wakra, education center facilities Qatar, smart classrooms Qatar"
            />

            {/* --------------------- Page Header --------------------- */}
            <section className="pt-16 pb-12 md:pt-20 md:pb-16 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 animate-slide-up">
                            Our Campus - Al Wakra, Qatar
                        </h1>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Modern, safe, and inspiring learning environment for K-12 students in Qatar
                        </p>
                    </div>
                </div>
            </section>

            {/* --------------------- Campus Overview --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">World-Class Facilities in Al Wakra</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Tuition Point Education Center is located in Al Wakra, Qatar, designed to provide a comfortable and stimulating learning environment for CBSE, IGCSE, and IB students. Our modern facilities inspire creativity, foster collaboration, and support academic growth for K-12 learners.
                        </p>
                    </div>
                </div>
            </section>

            {/* --------------------- Facilities --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Campus Facilities - Qatar</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Everything students need for effective learning and board exam preparation
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {facilities.map((facility, index) => (
                            <FacilityCard key={index} facility={facility} delay={(index % 3) + 1} />
                        ))}
                    </div>
                </div>
            </section>

            {/* --------------------- Location & Access --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Location & Access</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Conveniently located in Al Wakra with easy access from all parts of Qatar
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Map */}
                        <div className="animate-slide-up">
                            <div className="rounded-xl overflow-hidden h-96 shadow-xl border border-border">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14457.06730303728!2d51.603306!3d25.176412!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e459032549d44f9%3A0xcf94b595232750e3!2sAl%20Wakra%20Qatar!5e0!3m2!1sen!2sin!4v1696078709040!5m2!1sen!2sin"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen={false}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Tuition Point Location Map"
                                ></iframe>
                            </div>
                        </div>

                        {/* Location Details */}
                        <div className="animate-slide-up animation-delay-1">
                            <div className="bg-white rounded-xl shadow-lg text-foreground p-8">
                                <h3 className="text-2xl font-display font-bold mb-6">Visit Our Campus in Al Wakra</h3>

                                <div className="space-y-6">
                                    <div className="flex items-start gap-4">
                                        <MapPin className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                                        <div>
                                            <h4 className="font-bold mb-1">Address</h4>
                                            <p className="text-muted-foreground">
                                                Villa No. 99, Street - 201, Zone - 90<br />
                                                Al Wakra, Qatar
                                            </p>
                                            <p className="text-sm text-muted-foreground mt-2">
                                                Near Al Wakra Hospital and Central Market
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <Car className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                                        <div>
                                            <h4 className="font-bold mb-1">Parking</h4>
                                            <p className="text-muted-foreground">
                                                Ample parking space available for parents dropping off and picking up students
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <Bus className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                                        <div>
                                            <h4 className="font-bold mb-1">Public Transport</h4>
                                            <p className="text-muted-foreground">
                                                Easily accessible via public transportation routes in Al Wakra and Doha
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --------------------- Operating Hours --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">Operating Hours - Al Wakra</h2>
                        <p className="text-lg text-white/80 animate-slide-up">
                            Flexible tutoring hours for CBSE, IGCSE, and IB students in Qatar
                        </p>
                    </div>

                    <div className="max-w-2xl mx-auto">
                        <div className="bg-white rounded-xl shadow-lg text-foreground p-8 animate-fade-in">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-14 h-14 rounded-full bg-cyan-100 flex items-center justify-center">
                                    <Clock className="w-7 h-7 text-accent" />
                                </div>
                                <h3 className="text-2xl font-display font-bold">Weekly Schedule</h3>
                            </div>

                            <div className="space-y-4">
                                {operatingHours.map((schedule, index) => (
                                    <div
                                        key={index}
                                        className={`flex items-center justify-between p-4 rounded-lg ${
                                            schedule.highlight ? 'bg-muted' : 'bg-transparent border border-border'
                                        }`}
                                    >
                                        <span className="font-display font-semibold">{schedule.day}</span>
                                        <span className={schedule.highlight ? 'text-muted-foreground' : 'text-accent font-semibold'}>
                                            {schedule.hours}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <p className="text-sm text-muted-foreground mt-6 text-center">
                                Extended hours available during board exam season in Qatar
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --------------------- Campus Visit CTA --------------------- */}
            <section className="py-24 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 animate-slide-up">
                            Schedule a Campus Visit in Al Wakra
                        </h2>
                        <p className="text-lg text-white text-opacity-90 mb-8 animate-slide-up">
                            Tour our modern facilities and meet our expert CBSE, IGCSE, and IB teachers in Qatar
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
                            <Link
                                to="/contact"
                                className="bg-white text-accent hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300 sm:w-auto text-lg"
                            >
                                Book a Visit
                            </Link>
                            <Link
                                to="/enrollment"
                                className="border-2 border-white text-white hover:bg-white hover:text-accent font-semibold py-3 px-8 rounded-lg transition duration-300 sm:w-auto text-lg"
                            >
                                Enroll Now
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CampusPage;
